import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Code, Copy, Plus, StretchHorizontal, X } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

interface EnvironmentVariable {
  key: string;
  value: string;
}

interface EnvironmentVariablesProps {
  ctaLabel?: string;
  environmentVars: EnvironmentVariable[];
  onEnvironmentVarsChange: (vars: EnvironmentVariable[]) => void;
  disabled: boolean;
  requiredKeys?: string[];
}

export function EnvironmentVariables({
  ctaLabel = "Add Environment Variable",
  environmentVars,
  onEnvironmentVarsChange,
  disabled,
  requiredKeys = [],
}: EnvironmentVariablesProps) {
  const addEnvironmentVar = () => {
    onEnvironmentVarsChange([...environmentVars, { key: "", value: "" }]);
  };

  const updateEnvironmentVar = (
    index: number,
    field: "key" | "value",
    value: string,
  ) => {
    if (field === "key" && requiredKeys.includes(environmentVars[index].key)) {
      return;
    }
    const newVars = environmentVars.map((env, i) =>
      i === index ? { ...env, [field]: value } : env,
    );
    onEnvironmentVarsChange(newVars);
  };

  const removeEnvironmentVar = (index: number) => {
    if (requiredKeys.includes(environmentVars[index].key)) {
      return;
    }
    const newVars = environmentVars.filter((_, i) => i !== index);
    onEnvironmentVarsChange(newVars);
  };

  const generatePlainText = (vars: EnvironmentVariable[]) =>
    vars
      .map((env) => (env.value ? `${env.key}=${env.value}` : env.key))
      .join("\n");

  const [textValue, setTextValue] = useState(
    generatePlainText(environmentVars),
  );
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setTextValue(generatePlainText(environmentVars));
  }, [environmentVars]);

  const syncToParent = (text: string) => {
    const newVars = text.split("\n").map((line) => {
      const separatorIndex = line.indexOf("=");
      if (separatorIndex === -1) {
        return { key: line.trim(), value: "" };
      }
      const key = line.slice(0, separatorIndex).trim();
      const value = line.slice(separatorIndex + 1);
      return { key, value };
    });

    const missingRequiredVars = environmentVars.filter(
      (env) =>
        requiredKeys.includes(env.key) &&
        !newVars.some((newEnv) => newEnv.key === env.key),
    );

    onEnvironmentVarsChange([...newVars, ...missingRequiredVars]);
  };

  return (
    <div className="space-y-4 border-t pt-4">
      <Tabs
        defaultValue="ui"
        className="w-full"
        onValueChange={(value) => {
          if (value === "ui") {
            syncToParent(textValue);
          }
        }}
      >
        <div className="flex">
          <div className="grow">
            <h4 className="mb-1 text-sm font-medium text-muted-foreground">
              Environment Variables
            </h4>
            <p className="text-xs text-muted-foreground">
              Configure environment variables for your project
            </p>
          </div>
          <TabsList>
            <TabsTrigger value="ui">
              <StretchHorizontal />
            </TabsTrigger>
            <TabsTrigger value="text">
              <Code />
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="ui">
          <div className="mt-4 space-y-4">
            {environmentVars.map((env, index) => {
              const isRequired = requiredKeys.includes(env.key);
              return (
                <div key={index} className="flex items-start gap-3">
                  <div className="flex-1">
                    <Input
                      placeholder="VARIABLE_NAME"
                      value={env.key}
                      onChange={(e) =>
                        !isRequired &&
                        updateEnvironmentVar(index, "key", e.target.value)
                      }
                      className="bg-sidebar font-mono"
                      disabled={disabled || isRequired}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      placeholder="value"
                      value={env.value}
                      onChange={(e) =>
                        updateEnvironmentVar(index, "value", e.target.value)
                      }
                      className="bg-sidebar font-mono"
                      disabled={disabled}
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => !isRequired && removeEnvironmentVar(index)}
                    disabled={disabled || isRequired}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
            <Button
              variant="outline"
              onClick={addEnvironmentVar}
              className="w-full"
              disabled={disabled}
            >
              <Plus className="mr-2 h-4 w-4" />
              {ctaLabel}
            </Button>
          </div>
        </TabsContent>
        <TabsContent value="text">
          <div className="relative mt-4">
            <form onSubmit={(e) => e.preventDefault()}>
              <Textarea
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                onBlur={() => syncToParent(textValue)}
                placeholder={`VAR1=value1\nVAR2=value2`}
                className="h-40 pr-10 font-mono"
                disabled={disabled}
              />
            </form>
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-8 w-auto px-2 text-muted-foreground"
              onClick={() => {
                navigator.clipboard.writeText(textValue);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
              disabled={disabled}
            >
              {copied ? "Copied!" : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
